import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import AbsenceInfoButton from './absenceInfoButton';
import ActionButton from '../../actionButton';
import DotsMenu from '../../dotsMenu';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit-dark.svg';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import { ABSENCE_FIELD_NAMES } from '../../../../constants/absence';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    borderRadius: 16,
    backgroundColor: ({ color }) => color || primary.bluish8,
    padding: spacing(2, 4),
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  carryoverWrapper: {
    paddingTop: spacing(4),
  },
  iconWrapper: {
    backgroundColor: primary.white,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: spacing(3),
    width: 56,
    height: 56,
  },
  content: {
    maxWidth: 'calc(100% - 68px)',
  },
  dateLabelWrapper: {
    maxWidth: 'calc(100% - 58px)',
  },
  daysWrapper: {
    marginBottom: spacing(1),
    display: 'flex',
  },
  space: {
    marginLeft: spacing(1),
  },
  dateLabel: {
    color: primary.black,
    display: 'flex',
    alignItems: 'center',
    marginLeft: spacing(1),
  },
  days: {
    minWidth: 'fit-content',
  },
  label: {
    color: primary.black,
    textTransform: 'lowercase',
    display: 'flex',
    alignItems: 'center',
  },
  name: {
    color: primary.indigo,
    textTransform: 'none',
  },
  balance: {
    color: primary.black,
    textTransform: 'capitalize',
  },
  actionButton: {
    position: 'absolute',
    right: 41,
    top: 8,
  },
  icon: {
    width: 24,
    height: 24,
  },
  infoIcon: {
    position: 'absolute',
    right: 16,
    top: 8,
  },
}));

const AbsenceCard = ({
  translations,
  absence,
  canManageBalance,
  getBreakdownData,
  onManageBalanceCurrentYear,
  onManageBalancePreviousYear,
}) => {
  const {
    NAME,
    COLOR,
    HAS_DAYS_ENABLED,
    AVAILABLE_DAYS,
    CAN_EDIT_PREVIOUS_YEAR_BALANCE,
    USED_DAYS,
    HAS_ROLLOVER,
  } = ABSENCE_FIELD_NAMES;
  const { absenceIcon: Icon } = absence;
  const classes = useStyles({ color: absence[COLOR] });
  const hasDaysEnabled = absence[HAS_DAYS_ENABLED];

  const menuItems = [
    {
      type: 'manageBalancePreviousYear',
      label: translations.menuItemLabels.manageBalancePreviousYear,
      shouldItemRender: () => true,
      action: onManageBalancePreviousYear,
      icon: EditIcon,
    },
    {
      type: 'manageBalanceCurrentYear',
      label: translations.menuItemLabels.manageBalanceCurrentYear,
      shouldItemRender: () => true,
      action: onManageBalanceCurrentYear,
      icon: EditIcon,
    },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.iconWrapper}>{Icon ? <Icon /> : null}</div>
      <div className={classes.content}>
        {hasDaysEnabled ? (
          <div>
            <div className={classes.daysWrapper}>
              <Typography className={classes.balance} variant="h3">
                {absence[AVAILABLE_DAYS]}
              </Typography>
              <Typography
                className={classNames(classes.label, classes.space)}
                variant="body1"
              >
                {`${
                  Math.abs(absence[AVAILABLE_DAYS]) !== 1
                    ? translations.days
                    : translations.day
                } ${translations.available}`}
              </Typography>
            </div>
            <div className={classes.daysWrapper}>
              <TextBoxWithTooltip text={absence[NAME]} variant="subtitle2" />
            </div>
          </div>
        ) : (
          <>
            <div className={classes.daysWrapper}>
              <Typography className={classes.balance} variant="h3">
                {`${Math.abs(absence[USED_DAYS])}`}
              </Typography>
              <Typography
                className={classNames(classes.label, classes.space)}
                variant="body1"
              >
                {`${
                  Math.abs(absence[USED_DAYS]) !== 1
                    ? translations.days
                    : translations.day
                } ${translations.used}`}
              </Typography>
            </div>
            <TextBoxWithTooltip variant="subtitle2" text={absence[NAME]} />
          </>
        )}
      </div>
      {canManageBalance &&
        hasDaysEnabled &&
        absence[HAS_ROLLOVER] &&
        absence[CAN_EDIT_PREVIOUS_YEAR_BALANCE] && (
          <DotsMenu className={classes.actionButton} menuItems={menuItems} />
        )}
      {canManageBalance &&
        hasDaysEnabled &&
        (!absence[HAS_ROLLOVER] ||
          !absence[CAN_EDIT_PREVIOUS_YEAR_BALANCE]) && (
          <ActionButton
            className={classes.actionButton}
            iconButtonClass={classes.icon}
            type={ACTION_BUTTON_TYPES.EDIT}
            tooltipText={translations.updateBalanceCurrentYear}
            onClickHandler={onManageBalanceCurrentYear}
          />
        )}
      {hasDaysEnabled && (
        <AbsenceInfoButton
          customClassName={classes.infoIcon}
          translations={translations}
          absence={absence}
          position="bottom-start"
          getBreakdownData={getBreakdownData}
          interactive
        />
      )}
    </div>
  );
};

AbsenceCard.propTypes = {
  translations: PropTypes.object.isRequired,
  absence: PropTypes.object.isRequired,
  canManageBalance: PropTypes.bool.isRequired,
  getBreakdownData: PropTypes.func.isRequired,
  onManageBalanceCurrentYear: PropTypes.func.isRequired,
  onManageBalancePreviousYear: PropTypes.func.isRequired,
};

export default AbsenceCard;
